import { useCallback, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";

import { ISalvage, ISetSalvageRequest, SalvageStage, useSaveSalvage } from "../../../data/useSalvage";
import { useUserLookup } from "../../../hooks/useUserLookup";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import PagedTable from "../../shared/Tables/PagedTable";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import openInNewTab from "../../../utils/openInNewTab";
import { salvageRequestFromRow } from "../../../utils/salvageMath";
import { SalvageCheckCell, SalvageDateCell, SalvageMoneyCell } from "./SalvageTableCells";

const columnHelper = createColumnHelper<ISalvage>();

function money(value?: number | null, canViewPrices?: boolean) {
  if (!canViewPrices || value == null) return "—";
  return PriceFormat(value);
}

export default function SalvageTable({
  customerId,
  canViewPrices,
  stage,
}: {
  customerId: string;
  canViewPrices: boolean;
  stage?: SalvageStage;
}) {
  const { findUser } = useUserLookup();
  const save = useSaveSalvage(customerId, "SalvageTable");
  const { saveHandlers } = useDefaultCRUDHandlers("Salvage");

  const patchRow = useCallback(
    (row: ISalvage, patch: Partial<ISetSalvageRequest>) => {
      save.mutate({ leadId: row.leadId, body: salvageRequestFromRow(row, patch) }, { onError: saveHandlers.onError });
    },
    [save, saveHandlers.onError]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.vrm ?? "", {
        id: "vrm",
        header: "Vehicle",
        cell: (info) => {
          const row = info.row.original;
          const description = [row.make, row.model].filter(Boolean).join(" ");
          return (
            <button
              type="button"
              className="text-left"
              onClick={() => openInNewTab(`/bookings/${row.leadId}/salvage`)}
            >
              <div className="font-medium text-primary-700 hover:underline">{row.vrm || "—"}</div>
              {description && <div className="text-xs text-gray-500">{description}</div>}
            </button>
          );
        },
      }),
      columnHelper.accessor((row) => row.bookingDate ?? "", {
        id: "bookingDate",
        header: "Booking date",
        cell: (info) => (info.getValue() ? formatDateTime(info.getValue(), DateTime.DATE_SHORT) : "—"),
      }),
      columnHelper.display({
        id: "bookedBy",
        header: "Booked by",
        cell: (info) => {
          const userId = info.row.original.bookedCollectionBy;
          if (!userId) return "—";
          const user = findUser(userId);
          return user?.fullname?.trim() || "—";
        },
      }),
      columnHelper.accessor((row) => row.vehiclePrice ?? "", {
        id: "vehiclePrice",
        header: "Purchase price",
        cell: (info) => money(info.row.original.vehiclePrice, canViewPrices),
      }),
      columnHelper.accessor((row) => row.copartCollectedOn ?? "", {
        id: "copartCollectedOn",
        header: "Collected by Copart",
        cell: (info) => (
          <SalvageDateCell
            value={info.row.original.copartCollectedOn}
            onCommit={(copartCollectedOn) => patchRow(info.row.original, { copartCollectedOn })}
          />
        ),
      }),
      columnHelper.display({
        id: "daysInStock",
        header: "Days in stock",
        cell: (info) => info.row.original.daysInStock ?? "—",
      }),
      columnHelper.display({
        id: "collectionCost",
        header: "Collection cost",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.collectionCost}
            disabled={!canViewPrices}
            onCommit={(collectionCost) => patchRow(info.row.original, { collectionCost })}
          />
        ),
      }),
      columnHelper.display({
        id: "capValue",
        header: "CAP value",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.capValue}
            disabled={!canViewPrices}
            onCommit={(capValue) => patchRow(info.row.original, { capValue })}
          />
        ),
      }),
      columnHelper.display({
        id: "proquoteValue",
        header: "Retail value",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.proquoteValue}
            disabled={!canViewPrices}
            onCommit={(proquoteValue) => patchRow(info.row.original, { proquoteValue })}
          />
        ),
      }),
      columnHelper.accessor((row) => row.projectedAuctionValue ?? "", {
        id: "projectedAuctionValue",
        header: "Projected auction",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.projectedAuctionValue}
            disabled={!canViewPrices}
            onCommit={(projectedAuctionValue) => patchRow(info.row.original, { projectedAuctionValue })}
          />
        ),
      }),
      columnHelper.display({
        id: "auctionRun1",
        header: "Run 1",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.auctionRun1}
            disabled={!canViewPrices}
            onCommit={(auctionRun1) => patchRow(info.row.original, { auctionRun1 })}
          />
        ),
      }),
      columnHelper.display({
        id: "auctionRun2",
        header: "Run 2",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.auctionRun2}
            disabled={!canViewPrices}
            onCommit={(auctionRun2) => patchRow(info.row.original, { auctionRun2 })}
          />
        ),
      }),
      columnHelper.display({
        id: "auctionRun3",
        header: "Run 3",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.auctionRun3}
            disabled={!canViewPrices}
            onCommit={(auctionRun3) => patchRow(info.row.original, { auctionRun3 })}
          />
        ),
      }),
      columnHelper.display({
        id: "auctionRun4",
        header: "Run 4",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.auctionRun4}
            disabled={!canViewPrices}
            onCommit={(auctionRun4) => patchRow(info.row.original, { auctionRun4 })}
          />
        ),
      }),
      columnHelper.accessor((row) => row.actualAuctionValue ?? "", {
        id: "actualAuctionValue",
        header: "Actual auction",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.actualAuctionValue}
            disabled={!canViewPrices}
            onCommit={(actualAuctionValue) => patchRow(info.row.original, { actualAuctionValue })}
          />
        ),
      }),
      columnHelper.accessor((row) => row.soldOn ?? "", {
        id: "soldOn",
        header: "Date sold",
        cell: (info) => (
          <SalvageDateCell
            value={info.row.original.soldOn}
            onCommit={(soldOn) => patchRow(info.row.original, { soldOn })}
          />
        ),
      }),
      columnHelper.accessor((row) => row.returnedToGp, {
        id: "returnedToGp",
        header: "Returned to GP",
        enableSorting: false,
        cell: (info) => (
          <SalvageCheckCell
            checked={!!info.getValue()}
            onCommit={(returnedToGp) => patchRow(info.row.original, { returnedToGp })}
          />
        ),
      }),
      columnHelper.display({
        id: "redeliveryCost",
        header: "Redelivery cost",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.redeliveryCost}
            disabled={!canViewPrices}
            onCommit={(redeliveryCost) => patchRow(info.row.original, { redeliveryCost })}
          />
        ),
      }),
      columnHelper.display({
        id: "finalSellingFees",
        header: "Selling fees",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.finalSellingFees}
            disabled={!canViewPrices}
            onCommit={(finalSellingFees) => patchRow(info.row.original, { finalSellingFees })}
          />
        ),
      }),
      columnHelper.display({
        id: "vatCharges",
        header: "VAT charges",
        cell: (info) => (
          <SalvageMoneyCell
            value={info.row.original.vatCharges}
            disabled={!canViewPrices}
            onCommit={(vatCharges) => patchRow(info.row.original, { vatCharges })}
          />
        ),
      }),
      columnHelper.accessor((row) => row.profit ?? "", {
        id: "profit",
        header: "Profit",
        cell: (info) => money(info.row.original.profit, canViewPrices),
      }),
    ],
    [canViewPrices, findUser, patchRow]
  );

  return (
    <PagedTable<ISalvage>
      columns={columns}
      url={`/sav/customer/${customerId}/salvage`}
      queryLabel="Search VRM or name"
      initialSort={[{ id: "bookingDate", desc: true }]}
      additionalBody={stage ? { stage } : undefined}
    />
  );
}
